<script setup lang="ts">
/**
 * A route's number on the mode's colour (PTV's tram green for Melbourne's trams): the badge on the front of the
 * tram and on every stop's pole, and the one way a tram row says what it is.
 *
 * WHERE A TRAIN ROW HAS ITS COLOUR BAR, a tram row has this. The train palette
 * tells a commuter which group a line belongs to; a tram route's group is "the
 * trams", and what tells two apart is the number — so the number is what the
 * colour carries. It is the only saturated thing on a tram row, which keeps the
 * app's one rule: the colour belongs to the line.
 *
 * Dark ink on the green in both schemes, because PTV prints it that way and a
 * white numeral on #78be20 fails contrast.
 */

withDefaults(defineProps<{
  /** The mode's registry declaration: its badge colour. */
  mode: { badge: { colour: string | null; text?: string } }
  /** The route number, "96". */
  number: string
  /** `row` on the ledger and the board, `small` inline in a sentence or a trip. */
  size?: 'row' | 'small' | 'large'
  /** Read out instead of the bare number, where the row does not say it. */
  label?: string
}>(), { size: 'row', label: '' })
</script>

<template>
  <span
    class="tram-badge"
    :class="`tram-badge-${size}`"
    :style="{ background: mode.badge.colour ?? undefined, color: mode.badge.text ?? undefined }"
    :aria-label="label || undefined"
    :aria-hidden="label ? undefined : 'true'"
  >{{ number }}</span>
</template>

<style scoped>
.tram-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* The ink is the dark scheme's carriage in both schemes: PTV prints the
     number dark on the green, and a white one fails contrast. */
  color: #14161a;
  font-family: var(--la-font-display);
  font-stretch: 100%;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  letter-spacing: 0;
}

.tram-badge-row {
  min-width: 44px;
  height: 32px;
  padding: 0 7px;
  border-radius: 8px;
  font-size: 17px;
}

.tram-badge-small {
  min-width: 26px;
  height: 20px;
  padding: 0 5px;
  border-radius: 5px;
  font-size: 12px;
}

.tram-badge-large {
  min-width: 56px;
  height: 40px;
  padding: 0 9px;
  border-radius: 10px;
  font-size: 22px;
}
</style>
